// src\routes\superVisorRoutes.tsx
import { lazy, Suspense } from "react";
import { PageContentSkeleton } from "@/components/skeletons";
import { RouteObject, Navigate } from "react-router-dom";
import { SuperVisorLayout } from "@/layouts";
import CrmCustomerWriteGuard from "@/components/CrmCustomerWriteGuard";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { SuperVisor } from "@/types";
// import AddKnowledgeBase from "@/pages/SuperVisor/AddKnowledgeBase";

const SuperVisorDashboard = lazy(() => import("@/pages/SuperVisor/Dashboard"));
const AddAgent = lazy(() => import("@/pages/SuperVisor/AddAgent"));
const AgentList = lazy(() => import("@/pages/SuperVisor/AgentList"));
const Groups = lazy(() => import("@/pages/SuperVisor/Groups/ManageGroups/Groups"));
const GroupDetails = lazy(() => import("@/pages/SuperVisor/Groups/ManageGroups/GroupDetails"));
const GroupMembers = lazy(() => import("@/pages/SuperVisor/Groups/Members/Members"));
const GroupTicketList = lazy(() => import("@/pages/SuperVisor/Groups/Tickets/GroupTicketList"));
const GroupTicketDetails = lazy(() => import("@/pages/SuperVisor/Groups/Tickets/GroupTicketDetails"));
const AttendanceList = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/AttandanceOverview/AttendanceList"));
const StaffList = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/AttandanceOverview/StaffList"));
const StaffDetails = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/AttandanceOverview/StaffDetails"));
const Feedback = lazy(() => import("@/pages/SuperVisor/Feedbacks/Feedback"));
const Tickets = lazy(() => import("@/pages/SuperVisor/Ticket/TicketList"));
const ticketDetails = lazy(() => import("@/pages/SuperVisor/Ticket/ticketDetails"));
const TicketCategories = lazy(() => import("@/pages/SuperVisor/Ticket/TicketCategoryList"));
const TicketSubCategories = lazy(() => import("@/pages/SuperVisor/Ticket/TicketSubCategoryList"));
const TicketSubSubCategories = lazy(() => import("@/pages/SuperVisor/Ticket/TicketSubSubCategoryList"));
const NotificationList = lazy(() => import("@/pages/Common/NotificationList"));
const Profile = lazy(() => import("@/pages/SuperVisor/Profile"));
const Subscription = lazy(() => import("@/pages/SuperVisor/Subscription/index"));
const GlobalSettings = lazy(() => import("@/pages/SuperVisor/GlobalSettings"));
const CustomScripts = lazy(() => import("@/pages/SuperVisor/CustomScripts"));
const ApiDocs = lazy(() => import("@/pages/SuperVisor/ApiDocs"));
const TicketingSystemSettings = lazy(() => import("@/pages/SuperVisor/TicketingSystemSettings"));
const CustomerSatisfaction = lazy(() => import("@/pages/SuperVisor/CustomerSatisfaction"));
const KnowledgeBase = lazy(() => import("@/pages/Public/KnowledgeBase"));
const AgentReport = lazy(() => import("@/pages/SuperVisor/Reports/AgentCallActivityReport"));
const AgentTicketReport = lazy(() => import("@/pages/SuperVisor/Reports/AgentTicketReport"));
const AgentAttendanceReport = lazy(() => import("@/pages/SuperVisor/Reports/AgentAttendanceReport"));
const CSATDashboard = lazy(() => import("@/pages/SuperVisor/Reports/CSATDashboard"));
const TicketCallAnalytics = lazy(() => import("@/pages/SuperVisor/Reports/TicketCallAnalytics"));
const AgentAnalytics = lazy(() => import("@/pages/SuperVisor/Reports/AgentAnalytics"));
const GroupTicketReport = lazy(() => import("@/pages/SuperVisor/Reports/GroupTicketReport"));
const AgentTicketDetailReportPerAgent = lazy(() => import("@/pages/SuperVisor/Reports/AgentTicketDetailReportPerAgent"));
const AddKnowledgeBase = lazy(() => import("@/pages/SuperVisor/AddKnowledgeBase"));

const UpdateKnowledgeBase = lazy(() => import("@/pages/SuperVisor/UpdateKnowledgeBase"));
const CrmCustomerList = lazy(() => import("@/pages/SuperVisor/Crm/Customers/Customer"));
const CrmCustomerAddUpdate = lazy(() => import("@/pages/SuperVisor/Crm/Customers/AddEditCustomer"));
const CustomerViewDetails = lazy(() => import("@/pages/SuperVisor/Crm/Customers/CustomerViewDetails"));
const RequestList = lazy(() => import("@/pages/SuperVisor/Crm/RequestList/Request"));

// Custom Fields
const CustomFieldList = lazy(() => import("@/pages/SuperVisor/Crm/CustomFields/CustomFieldList"));
const AddEditCustomField = lazy(() => import("@/pages/SuperVisor/Crm/CustomFields/AddEditCustomField"));
const CustomFieldDetails = lazy(() => import("@/pages/SuperVisor/Crm/CustomFields/CustomFieldDetails"));

const EmailTempeleteCreate = lazy(() => import("@/pages/SuperVisor/Marketing/EmailTempelete/EmailTempeleteCreate"));
const EmailTempelteList = lazy(() => import("@/pages/SuperVisor/Marketing/EmailTempelete/EmailTempelteList"));
const ProspectList = lazy(() => import("@/pages/SuperVisor/Marketing/Prospects/ProspectList"));
const CreateProspect = lazy(() => import("@/pages/SuperVisor/Marketing/Prospects/CreateProspect"));
const SegmentList = lazy(() => import("@/pages/SuperVisor/Marketing/Segments/SigementList"));
const CreateSegment = lazy(() => import("@/pages/SuperVisor/Marketing/Segments/SegmentAddEdit"));
const CampaignList = lazy(() => import("@/pages/SuperVisor/Marketing/Campaings/CampaingList"));
const CreateCampaign = lazy(() => import("@/pages/SuperVisor/Marketing/Campaings/AddEdit/CreateCampaing"));
const FacebookPostThreadsList = lazy(() => import("@/pages/Agent/FacebookPostThreads/FacebookPostThreadsList"));
const FacebookPostThreadDetails = lazy(() => import("@/pages/Agent/FacebookPostThreads/FacebookPostThreadDetails"));
// const TicketStatus = lazy(() => import("@/pages/SuperVisor/TicketStatus"));
const ChatBotQuestions = lazy(() => import("@/pages/SuperVisor/ChatBotQuestions"));
const SLASettings = lazy(() => import("@/pages/SuperVisor/SlaSetting/SlaSettings"));
const SLADashboard = lazy(() => import("@/pages/SuperVisor/SlaSetting/SlaDashboard/index"));
const CrmSales = lazy(() => import("@/pages/SuperVisor/Sales/Sales/Sales"));
const CrmSalesAdd = lazy(() => import("@/pages/SuperVisor/Sales/Sales/AddDeal"));
const DealConversation = lazy(() => import("@/pages/SuperVisor/Sales/Sales/DealConversation"));
const Leads = lazy(() => import("@/pages/SuperVisor/Sales/SalesLead/Leads"));
const LeadsAddUpdate = lazy(() => import("@/pages/SuperVisor/Sales/SalesLead/LeadsAddUpdate"));
const AvailableWhatsappTempelate = lazy(() => import("@/pages/SuperVisor/Whatsapp/AvailableWhatsappTempelate"));
const ConnectWhatsAppForTenent = lazy(() => import("@/pages/SuperVisor/Whatsapp/ConnectWhatsAppForTenent"));
const SalesQuery = lazy(() => import("@/pages/Agent/Crm/SalesQuery/SalesQuery"));
const EditWhatsappTempelate = lazy(() => import("@/pages/SuperVisor/Whatsapp/EditwhatsAppTempelate"));

//workforce
const AttandanceDashbaord = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/ShiftLeaveRequestActivityOverview/AttandanceDashbaord"));
const SwapRequest = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/ShiftLeaveRequestActivityOverview/SwapRequest"));
const LeaveRequest = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/ShiftLeaveRequestActivityOverview/LeaveRequest"));
const ShiftManagement = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/ShiftManagement/ShiftManagement"));
const RulesAndSettings = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/ShiftSettings/RulesAndSettings"));
const ShiftCalendar = lazy(() => import("@/pages/SuperVisor/WorkForceAndShift/ShiftCalendar/ShiftCalendar"));

//crm
const CategoryList = lazy(() => import("@/pages/SuperVisor/Sales/Category/CategoryList"));
const AddEditCategory = lazy(() => import("@/pages/SuperVisor/Sales/Category/AddEditCategory"));
const SubCategoryList = lazy(() => import("@/pages/SuperVisor/Sales/SubCategory/SubCategoryList"));
const AddEditSubCategory = lazy(() => import("@/pages/SuperVisor/Sales/SubCategory/AddEditSubCategory"));
const RegionList = lazy(() => import("@/pages/SuperVisor/Sales/Region/RegionList"));
const AddEditRegion = lazy(() => import("@/pages/SuperVisor/Sales/Region/AddEditRegion"));
const SalesDashbaord = lazy(() => import("@/pages/SuperVisor/Sales/Dashbaord/SalesDashbaord"));

const FeedbackDetails = lazy(() => import("@/pages/SuperVisor/Feedbacks/FeedbackDetails"));

//manager
const ManagerList = lazy(() => import("@/pages/SuperVisor/Manager/ManagerList"));
const AddUpdateManager = lazy(() => import("@/pages/SuperVisor/Manager/AddUpdateManager"));

const MarketingReport = lazy(() => import("@/pages/SuperVisor/Reports/MarketingReport"));
const MicrosoftOAuthCallback = lazy(() => import("@/pages/SuperVisor/MicrosoftOAuthCallback"));

const InstagramPostThreadsList = lazy(() => import("@/pages/Agent/InstagramThread/InstagramPostThreadsList"));
const InstagramPostThreadDetails = lazy(() => import("@/pages/Agent/InstagramThread/InstagramPostThreadDetails"));

const FacebookConversationsList = lazy(() => import("@/pages/SuperVisor/FacebookConversations/FacebookConversationsList"));
const FacebookConversationDetails = lazy(() => import("@/pages/SuperVisor/FacebookConversations/FacebookConversationDetails"));

const InstagramConversationsList = lazy(() => import("@/pages/SuperVisor/InstagramConversations/InstagramConversationsList"));
const InstagramConversationDetails = lazy(() => import("@/pages/SuperVisor/InstagramConversations/InstagramConversationDetails"));

const WhatsappConversationsList = lazy(() => import("@/pages/SuperVisor/Whatsapp/Conversations/WhatsappConversationsList"));
const WhatsappConversationDetails = lazy(() => import("@/pages/SuperVisor/Whatsapp/Conversations/WhatsappConversationDetails"));

const FacebookTemplatesList = lazy(() => import("@/pages/SuperVisor/SocialTemplates/FacebookTemplatesList"));
const FacebookTemplateEdit = lazy(() => import("@/pages/SuperVisor/SocialTemplates/FacebookTemplateEdit"));
const InstagramTemplatesList = lazy(() => import("@/pages/SuperVisor/SocialTemplates/InstagramTemplatesList"));
const InstagramTemplateEdit = lazy(() => import("@/pages/SuperVisor/SocialTemplates/InstagramTemplateEdit"));
const CustomerInquiryList = lazy(() => import("@/pages/Agent/CustomerInquiryList"));

//retention
const RetentionPolicies = lazy(() => import("@/pages/SuperVisor/Retention/RetentionPolicies"));
const ArchiveRecords = lazy(() => import("@/pages/SuperVisor/Retention/ArchiveRecords"));
const ArchiveRecordDetails = lazy(() => import("@/pages/SuperVisor/Retention/ArchiveRecordDetails"));
const LegalHolds = lazy(() => import("@/pages/SuperVisor/Retention/LegalHolds"));
const RetentionAuditTrail = lazy(() => import("@/pages/SuperVisor/Retention/RetentionAuditTrail"));
const AgentInquiryList = lazy(() => import("@/pages/SuperVisor/AgentInquiryList"));


const withSuspense = (Component: React.FC) => (
  <Suspense fallback={<PageContentSkeleton />}>
    <Component />
  </Suspense>
);

const GroupMemberTicketList = () => <GroupTicketList view="member-tickets" />;

const MainDashboard = () => {
  const { user, isSalesManager, isMarketingManager } = useRolePermissions<SuperVisor | null>();

  const hasMenu = (menuId: number) => {
    return user?.assignMenu?.some((item: any) => item?.menu?.id === menuId);
  };

  if (isSalesManager && hasMenu(28)) {
    return <Navigate to="/sales-dashboard" replace />;
  } if (isMarketingManager && hasMenu(50)) {
    return <Navigate to="/marketing-report" replace />;
  }
  return <SuperVisorDashboard />;
};

export const superVisorRoutes: RouteObject[] = [
  {
    path: "/",
    element: <SuperVisorLayout />,
    children: [
      { path: "", element: withSuspense(MainDashboard) },
      { path: "dashboard", element: withSuspense(MainDashboard) },
      { path: "subscription", element: withSuspense(Subscription) },
      { path: "profile", element: withSuspense(Profile) },

      { path: "tickets", element: withSuspense(Tickets) },
      { path: "tickets/:id", element: withSuspense(ticketDetails) },
      { path: "ticket-categories", element: withSuspense(TicketCategories) },
      { path: "ticket-sub-categories", element: withSuspense(TicketSubCategories) },
      { path: "ticket-sub-sub-categories", element: withSuspense(TicketSubSubCategories) },

      { path: "agents-report", element: withSuspense(AgentReport) },
      { path: "agents-enquery", element: withSuspense(AgentInquiryList) },
      { path: "agents-ticket-report", element: withSuspense(AgentTicketReport) },
      { path: "agents-ticket-report-details/:id", element: withSuspense(AgentTicketDetailReportPerAgent) },
      { path: "agents-attendance-report", element: withSuspense(AgentAttendanceReport) },
      { path: "csat-report", element: withSuspense(CSATDashboard) },
      { path: "ticket-call-analytics", element: withSuspense(TicketCallAnalytics) },
      { path: "agent-analytics-report", element: withSuspense(AgentAnalytics) },
      { path: "ticket-volume-report", element: withSuspense(GroupTicketReport) },
      { path: "time-in-group-report", element: <Navigate to="/ticket-volume-report" replace /> },
      { path: "marketing-report", element: withSuspense(MarketingReport) },
      { path: "sales-report", element: withSuspense(SalesDashbaord) },

      { path: "agents", element: withSuspense(AgentList) },
      { path: "agents/add", element: withSuspense(AddAgent) },
      { path: "agents/:id", element: withSuspense(AddAgent) },
      { path: "groups", element: withSuspense(Groups) },
      { path: "groups/details/:groupId", element: withSuspense(GroupDetails) },
      { path: "groups/members", element: withSuspense(GroupMembers) },
      { path: "groups/tickets", element: withSuspense(GroupTicketList) },
      { path: "groups/tickets/:id", element: withSuspense(GroupTicketDetails) },
      { path: "group-ticket", element: withSuspense(GroupMemberTicketList) },
      { path: "group-ticket/:id", element: withSuspense(GroupTicketDetails) },

      { path: "customer-query", element: withSuspense(CustomerInquiryList) },

      //manger
      { path: "managers", element: withSuspense(ManagerList) },
      { path: "manager/add", element: withSuspense(AddUpdateManager) },
      { path: "manager/update", element: withSuspense(AddUpdateManager) },


      //workforce
      { path: "workforce-overview", element: withSuspense(AttandanceDashbaord) },
      { path: "all-swap-requests", element: withSuspense(SwapRequest) },
      { path: "all-leave-requests", element: withSuspense(LeaveRequest) },
      { path: "shift-management", element: withSuspense(ShiftManagement) },
      { path: "rules-settings", element: withSuspense(RulesAndSettings) },
      { path: "shift-calendar", element: withSuspense(ShiftCalendar) },
      { path: "staffing-overview", element: withSuspense(AttendanceList) },
      { path: "staff-list", element: withSuspense(StaffList) },
      { path: "staff-details", element: withSuspense(StaffDetails) },
      // { path: "RulesAndSettings", element: withSuspense(RulesAndSettings) },

      { path: "/crm/customers", element: withSuspense(CrmCustomerList) },
      { path: "/crm/customers/add", element: <CrmCustomerWriteGuard>{withSuspense(CrmCustomerAddUpdate)}</CrmCustomerWriteGuard> },
      { path: "/crm/customers/edit/:id", element: <CrmCustomerWriteGuard>{withSuspense(CrmCustomerAddUpdate)}</CrmCustomerWriteGuard> },
      { path: "/crm/customers/details/:id", element: withSuspense(CustomerViewDetails) },
      { path: "/crm/requests", element: withSuspense(RequestList) },
      { path: "/crm/sales", element: withSuspense(CrmSales) },
      { path: "/crm/sales/add-deal", element: withSuspense(CrmSalesAdd) },
      { path: "/crm/sales/edit-deal", element: withSuspense(CrmSalesAdd) },
      { path: "/crm/leads", element: withSuspense(Leads) },
      { path: "/crm/leads/add", element: withSuspense(LeadsAddUpdate) },
      { path: "/crm/leads/edit", element: withSuspense(LeadsAddUpdate) },
      { path: "/crm/sales/conversation", element: withSuspense(DealConversation) },

      //crm
      { path: "/crm/category", element: withSuspense(CategoryList) },
      { path: "/crm/category/add", element: withSuspense(AddEditCategory) },
      { path: "/crm/category/edit", element: withSuspense(AddEditCategory) },
      { path: "/crm/sub-category", element: withSuspense(SubCategoryList) },
      { path: "/crm/sub-category/add", element: withSuspense(AddEditSubCategory) },
      { path: "/crm/sub-category/edit", element: withSuspense(AddEditSubCategory) },
      { path: "/crm/region", element: withSuspense(RegionList) },
      { path: "/crm/region/add", element: withSuspense(AddEditRegion) },
      { path: "/crm/region/edit", element: withSuspense(AddEditRegion) },
      { path: "/sales-dashboard", element: withSuspense(SalesDashbaord) },

      // Custom Fields Routes
      { path: "/crm/custom-fields", element: <CrmCustomerWriteGuard>{withSuspense(CustomFieldList)}</CrmCustomerWriteGuard> },
      { path: "/crm/custom-fields/add", element: <CrmCustomerWriteGuard>{withSuspense(AddEditCustomField)}</CrmCustomerWriteGuard> },
      { path: "/crm/custom-fields/edit/:fieldId", element: <CrmCustomerWriteGuard>{withSuspense(AddEditCustomField)}</CrmCustomerWriteGuard> },
      { path: "/crm/custom-fields/view/:fieldId", element: <CrmCustomerWriteGuard>{withSuspense(CustomFieldDetails)}</CrmCustomerWriteGuard> },
      { path: "/crm/sales-query", element: withSuspense(SalesQuery) },


      { path: "/email/template/create", element: withSuspense(EmailTempeleteCreate) },
      { path: "/email/template/update", element: withSuspense(EmailTempeleteCreate) },
      { path: "/email/templates", element: withSuspense(EmailTempelteList) },
      { path: "/segments", element: withSuspense(SegmentList) },
      { path: "/segment/add", element: withSuspense(CreateSegment) },
      { path: "/segment/update", element: withSuspense(CreateSegment) },

      { path: "/prospects", element: withSuspense(ProspectList) },
      { path: "/prospect/add", element: withSuspense(CreateProspect) },
      { path: "/prospect/edit", element: withSuspense(CreateProspect) },

      { path: "/campaigns", element: withSuspense(CampaignList) },
      { path: "/campaign/create", element: withSuspense(CreateCampaign) },
      { path: "/campaign/update", element: withSuspense(CreateCampaign) },

      { path: "/facebook-post-threads", element: withSuspense(FacebookPostThreadsList) },
      { path: "/facebook-post-threads/:threadId", element: withSuspense(FacebookPostThreadDetails) },

      { path: "/facebook-conversations", element: withSuspense(FacebookConversationsList) },
      { path: "/facebook-conversations/:conversationId", element: withSuspense(FacebookConversationDetails) },

      { path: "/instagram-conversations", element: withSuspense(InstagramConversationsList) },
      { path: "/instagram-conversations/:conversationId", element: withSuspense(InstagramConversationDetails) },

      { path: "/whatsapp-conversations", element: withSuspense(WhatsappConversationsList) },
      { path: "/whatsapp-conversations/:conversationId", element: withSuspense(WhatsappConversationDetails) },

      { path: "/facebook-templates", element: withSuspense(FacebookTemplatesList) },
      { path: "/facebook-templates/edit", element: withSuspense(FacebookTemplateEdit) },
      { path: "/instagram-templates", element: withSuspense(InstagramTemplatesList) },
      { path: "/instagram-templates/edit", element: withSuspense(InstagramTemplateEdit) },

      { path: "/instagram-post-threads", element: withSuspense(InstagramPostThreadsList) },
      { path: "/instagram-post-threads/:threadId", element: withSuspense(InstagramPostThreadDetails) },



      { path: "settings/chatbot-questions", element: withSuspense(ChatBotQuestions) },
      { path: "settings/ticketing-system", element: withSuspense(TicketingSystemSettings) },
      { path: "settings/global", element: withSuspense(GlobalSettings) },
      { path: "settings/custom-scripts", element: withSuspense(CustomScripts) },
      { path: "settings/sla-settings", element: withSuspense(SLASettings) },
      { path: "settings/sla-overview", element: withSuspense(SLADashboard) },
      { path: "settings/whatsapp", element: withSuspense(AvailableWhatsappTempelate) },
      { path: "settings/connect-whatsapp", element: withSuspense(ConnectWhatsAppForTenent) },
      { path: "settings/whatsapp/template/edit", element: withSuspense(EditWhatsappTempelate) },
      // { path: "settings/facebook", element: withSuspense(ConnectFacebook) },
      // { path: "settings/facebook/edit", element: withSuspense(EditFacebookForTenent) },
      { path: "oauth/callback/microsoft", element: withSuspense(MicrosoftOAuthCallback) },


      //retention
      { path: "/retention/policies", element: withSuspense(RetentionPolicies) },
      { path: "/retention/archive-records", element: withSuspense(ArchiveRecords) },
      { path: "/retention/archive-records/:id", element: withSuspense(ArchiveRecordDetails) },
      { path: "/retention/legal-holds", element: withSuspense(LegalHolds) },
      { path: "/retention/audit-trail", element: withSuspense(RetentionAuditTrail) },

      { path: "api-docs", element: withSuspense(ApiDocs) },
      { path: "feedback", element: withSuspense(Feedback) },
      { path: "feedback/details", element: withSuspense(FeedbackDetails) },
      { path: "notifications", element: withSuspense(NotificationList) },
      { path: "/knowledge-base", element: withSuspense(KnowledgeBase) },
      { path: "/knowledge-base/add", element: withSuspense(AddKnowledgeBase) },
      { path: "/knowledge-base/update", element: withSuspense(UpdateKnowledgeBase) },


      { path: "customer-satification", element: withSuspense(CustomerSatisfaction) },
    ],
  },
];
